/**
 * Application Router
 *
 * Main routing configuration using React Router v7.
 * All routes wrapped in DashboardLayout for consistent UI.
 *
 * @module routes
 */

import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { ROUTES } from './routes';

// Lazy load pages for code splitting
import { lazy, Suspense } from 'react';

// Layout
const DashboardLayout = lazy(() => import('@/components/layout/DashboardLayout/DashboardLayout'));
const FluidLayout = lazy(() => import('@/components/layout/FluidLayout/FluidLayout'));

// Pages
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const ProvinceDashboard = lazy(() => import('@/features/dashboard/pages/ProvinceDashboard'));
const RegionDashboard = lazy(() => import('@/features/dashboard/pages/RegionDashboard'));
const DistrictDashboard = lazy(() => import('@/features/dashboard/pages/DistrictDashboard'));
const TownDashboard = lazy(() => import('@/features/dashboard/pages/TownDashboard'));
const UCDashboard = lazy(() => import('@/features/dashboard/pages/UCDashboard'));


const DesignationsPage = lazy(() => import('@/features/designations/pages/DesignationsPage'));
const MemberRegistrationPage = lazy(() => import('@/features/memberRegistration/pages/MemberRegistrationPage'));
const TicketHolderPage = lazy(() => import('@/features/ticketHolder/pages/TicketHolderPage'));
const TicketHolderFormPage = lazy(() => import('@/features/ticketHolder/pages/TicketHolderFormPage'));
const TicketHolderDetailsPage = lazy(() => import('../features/ticketHolder/pages/TicketHolderDetailsPage'));
const RegionsPage = lazy(() => import('@/features/regions/pages/RegionsPage'));
const DistrictsPage = lazy(() => import('@/features/districts/pages/DistrictsPage'));
const TownsPage = lazy(() => import('@/features/towns/pages/TownsPage'));
const WingsPage = lazy(() => import('@/features/wings/pages/WingsPage'));
const UcsPage = lazy(() => import('@/features/ucs/pages/UcsPage'));
const PTIActivityPage = lazy(() => import('@/features/ptiActivity/pages/PTIActivityPage'));

// Fallback component for lazy loading
const PageLoader = () => (
  <div className="flex h-screen items-center justify-center">
    <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600"></div>
  </div>
);

/**
 * Application router configuration
 *
 * Uses lazy loading for code splitting and better performance.
 * All routes are wrapped in DashboardLayout.
 */
const router = createBrowserRouter([
  {
    element: (
      <Suspense fallback={<PageLoader />}>
        <FluidLayout />
      </Suspense>
    ),
    children: [
      {
        path: ROUTES.DESIGNATIONS.ROOT,
        element: (
          <Suspense fallback={<PageLoader />}>
            <DesignationsPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.MEMBER_REGISTRATION.ROOT,
        element: (
          <Suspense fallback={<PageLoader />}>
            <MemberRegistrationPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.TICKET_HOLDER.ROOT,
        element: (
          <Suspense fallback={<PageLoader />}>
            <TicketHolderPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.TICKET_HOLDER.CREATE,
        element: (
          <Suspense fallback={<PageLoader />}>
            <TicketHolderFormPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.TICKET_HOLDER.EDIT,
        element: (
          <Suspense fallback={<PageLoader />}>
            <TicketHolderFormPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.TICKET_HOLDER.DETAILS,
        element: (
          <Suspense fallback={<PageLoader />}>
            <TicketHolderDetailsPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.REGIONS.ROOT,
        element: (
          <Suspense fallback={<PageLoader />}>
            <RegionsPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.DISTRICTS.ROOT,
        element: (
          <Suspense fallback={<PageLoader />}>
            <DistrictsPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.WINGS.ROOT,
        element: (
          <Suspense fallback={<PageLoader />}>
            <WingsPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.TOWNS.ROOT,
        element: (
          <Suspense fallback={<PageLoader />}>
            <TownsPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.UCS.ROOT,
        element: (
          <Suspense fallback={<PageLoader />}>
            <UcsPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.PTI_ACTIVITY.ROOT,
        element: (
          <Suspense fallback={<PageLoader />}>
            <PTIActivityPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '/',
    element: (
      <Suspense fallback={<PageLoader />}>
        <DashboardLayout />
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageLoader />}>
            <DashboardPage />
          </Suspense>
        ),
      },


      {
        path: ROUTES.TEAMS.PROVINCE.slice(1),
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProvinceDashboard />
          </Suspense>
        ),
      },
      {
        path: ROUTES.TEAMS.REGION.slice(1),
        element: (
          <Suspense fallback={<PageLoader />}>
            <RegionDashboard />
          </Suspense>
        ),
      },
      {
        path: ROUTES.TEAMS.DISTRICT.slice(1),
        element: (
          <Suspense fallback={<PageLoader />}>
            <DistrictDashboard />
          </Suspense>
        ),
      },
      {
        path: ROUTES.TEAMS.TOWN.slice(1),
        element: (
          <Suspense fallback={<PageLoader />}>
            <TownDashboard />
          </Suspense>
        ),
      },
      {
        path: ROUTES.TEAMS.UC.slice(1),
        element: (
          <Suspense fallback={<PageLoader />}>
            <UCDashboard />
          </Suspense>
        ),
      },
    ],
  },
]);

/**
 * App Router Component
 *
 * Provides the router to the application.
 * Exported as the default to support Fast Refresh.
 */
export default function AppRouter() {
  return <RouterProvider router={router} />;
}